import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Lock, Save, Upload, Mail, Phone, Camera } from 'lucide-react';
import { guestService } from '../services/guestService';
import { Guest } from '../types/guest';
import { useIsMounted } from '../hooks/useIsMounted';
import { isValidEmail, isValidPhone } from '../utils/policeFormValidation';

// Interface pour les erreurs de validation
interface ValidationErrors {
  email?: string;
  phone?: string;
  identityPhotoUrl?: string;
}

const GuestEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [guest, setGuest] = useState<Guest | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Guest>>({});
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const isMounted = useIsMounted();

  // Validation des champs obligatoires pour la synchronisation CNS/ONT
  const validateMandatoryFields = (): ValidationErrors => {
    const errors: ValidationErrors = {};
    
    // Validation email obligatoire (Requirement 2.1)
    if (!formData.email || formData.email.trim() === '') {
      errors.email = 'L\'adresse email est obligatoire pour la synchronisation avec les autorités';
    } else if (!isValidEmail(formData.email)) {
      errors.email = 'Format d\'email invalide (ex: nom@domaine.com)';
    }
    
    // Validation téléphone obligatoire (Requirement 2.2)
    if (!formData.phone || formData.phone.trim() === '') {
      errors.phone = 'Le numéro de téléphone est obligatoire pour la synchronisation avec les autorités';
    } else if (!isValidPhone(formData.phone)) {
      errors.phone = 'Format de numéro de téléphone invalide (ex: +242066000000)';
    }
    
    // Validation photo d'identité obligatoire (Requirement 2.3)
    // Note: Le type Guest utilise documentPhotoUrl pour la photo d'identité
    if (!formData.documentPhotoUrl || formData.documentPhotoUrl.trim() === '') {
      errors.identityPhotoUrl = 'La photo de la pièce d\'identité est obligatoire pour la synchronisation avec les autorités';
    }
    
    return errors;
  };

  useEffect(() => {
    if (!id) {
      navigate('/guests');
      return;
    }

    let cancelled = false;

    const loadGuest = async () => {
      try {
        setLoading(true);
        setError(null);
        const guestData = await guestService.getGuest(id);
        if (!cancelled) {
          setGuest(guestData);
          setFormData(guestData);
        }
      } catch (err: any) {
        console.error('Erreur lors du chargement de l\'invité:', err);
        if (!cancelled) {
          const status = err?.response?.status;
          if (status === 404) {
            setError('Invité non trouvé');
          } else if (status === 401 || status === 403) {
            setError('Accès non autorisé');
          } else if (err?.code === 'ERR_NETWORK' || err?.message?.includes('Network Error')) {
            setError('Impossible de contacter le serveur. Vérifiez que les services backend sont démarrés.');
          } else {
            setError('Erreur lors du chargement des données de l\'invité');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadGuest();

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!guest) return;

    // Valider les champs obligatoires avant la soumission
    const errors = validateMandatoryFields();
    setValidationErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      // Afficher un message d'erreur global
      alert('Veuillez corriger les erreurs de validation avant de soumettre le formulaire.');
      return;
    }

    try {
      if (isMounted()) setSaving(true);
      await guestService.updateGuest(guest.id, formData);
      if (isMounted()) {
        navigate(`/guests/${guest.id}`);
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      if (isMounted()) {
        alert('Erreur lors de la mise à jour de l\'invité');
      }
    } finally {
      if (isMounted()) setSaving(false);
    }
  };

  // Gestion de l'upload de photo d'identité
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner un fichier image (JPEG, PNG)');
      return;
    }

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('La taille du fichier ne doit pas dépasser 5 Mo');
      return;
    }

    try {
      setUploadingPhoto(true);
      
      // Convertir en base64 pour l'aperçu et l'envoi
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        handleInputChange('documentPhotoUrl', base64);
        // Effacer l'erreur de validation si présente
        setValidationErrors(prev => ({ ...prev, identityPhotoUrl: undefined }));
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Erreur lors de l\'upload de la photo:', err);
      alert('Erreur lors de l\'upload de la photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleCancel = () => {
    navigate(`/guests/${id}`);
  };

  // Déterminer si les champs doivent être verrouillés
  const isFieldLocked = (fieldName: string): boolean => {
    if (!guest) return false;
    
    // Si la fiche police est clôturée, TOUS les champs sont verrouillés
    if (guest.policeFileStatus === 'Clôturée') {
      return true;
    }

    return false;
  };

  const handleInputChange = (field: keyof Guest, value: any) => {
    if (isFieldLocked(field)) return;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Effacer l'erreur de validation pour ce champ
    if (field === 'email' || field === 'phone' || field === 'documentPhotoUrl') {
      setValidationErrors(prev => ({ ...prev, [field === 'documentPhotoUrl' ? 'identityPhotoUrl' : field]: undefined }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des données de l'invité...</p>
        </div>
      </div>
    );
  };

  if (error || !guest) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Erreur</h3>
          <p className="text-gray-600 mb-4">{error || 'Invité non trouvé'}</p>
          <button
            onClick={() => navigate('/guests')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  const isFileClosed = guest.policeFileStatus === 'Clôturée';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* En-tête */}
        <div className="mb-8">
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour aux détails
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Modifier l'invité
              </h1>
              <p className="text-gray-600 mt-1">
                {guest.firstName} {guest.lastName} - {guest.passportNumber}
              </p>
            </div>
            
            {/* Indicateur de statut */}
            <div className="text-right">
              {/* Statut de la fiche police */}
              {guest.policeFileStatus && (
                <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium mb-2 ${
                  guest.policeFileStatus === 'Ouverte' ? 'bg-yellow-100 text-yellow-700' :
                  guest.policeFileStatus === 'Validée' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {guest.policeFileStatus === 'Clôturée' && <Lock className="w-3 h-3" />}
                  Fiche: {guest.policeFileStatus}
                </div>
              )}
              
              {/* Statut de l'invité */}
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                guest.status === 'CHECKED_IN' ? 'bg-green-100 text-green-700' :
                guest.status === 'CHECKED_OUT' ? 'bg-gray-100 text-gray-700' :
                guest.status === 'NO_SHOW' ? 'bg-red-100 text-red-700' :
                'bg-yellow-100 text-yellow-700'
              }`}>
                {guest.status === 'CHECKED_IN' ? 'Enregistré' :
                 guest.status === 'CHECKED_OUT' ? 'Parti' :
                 guest.status === 'NO_SHOW' ? 'Absent' :
                 'Réservé'
                }
              </div>
              
              {guest.hasAlerts && (
                <div className="flex items-center gap-1 mt-2 text-sm text-red-600">
                  <AlertTriangle className="w-4 h-4" />
                  {guest.alertCount} anomalie{guest.alertCount! > 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Avertissement pour les fichiers fermés */}
        {isFileClosed && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-red-800">Fiche Police Clôturée</h3>
                <p className="text-sm text-red-700 mt-1">
                  Cette fiche police est clôturée. Tous les champs sont verrouillés et ne peuvent plus être modifiés. 
                  La facture a été validée et les données ont été transmises aux autorités.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Formulaire d'édition */}
        <form onSubmit={handleSave} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="space-y-6">
            {/* Informations personnelles */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informations personnelles</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prénom *
                    {isFieldLocked('firstName') && <Lock className="inline w-3 h-3 ml-1" />}
                  </label>
                  <input
                    type="text"
                    value={formData.firstName || ''}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    disabled={isFieldLocked('firstName')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isFieldLocked('firstName') ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                    }`}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom *
                    {isFieldLocked('lastName') && <Lock className="inline w-3 h-3 ml-1" />}
                  </label>
                  <input
                    type="text"
                    value={formData.lastName || ''}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    disabled={isFieldLocked('lastName')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isFieldLocked('lastName') ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                    }`}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Mail className="inline w-4 h-4 mr-1" />
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      validationErrors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="nom@domaine.com"
                    required
                  />
                  {validationErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Phone className="inline w-4 h-4 mr-1" />
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      validationErrors.phone ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="+242066000000"
                    required
                  />
                  {validationErrors.phone && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.phone}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de naissance
                    {isFieldLocked('dateOfBirth') && <Lock className="inline w-3 h-3 ml-1" />}
                  </label>
                  <input
                    type="date"
                    value={formData.dateOfBirth || ''}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    disabled={isFieldLocked('dateOfBirth')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isFieldLocked('dateOfBirth') ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                    }`}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nationalité
                    {isFieldLocked('nationality') && <Lock className="inline w-3 h-3 ml-1" />}
                  </label>
                  <input
                    type="text"
                    value={formData.nationality || ''}
                    onChange={(e) => handleInputChange('nationality', e.target.value)}
                    disabled={isFieldLocked('nationality')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isFieldLocked('nationality') ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* Document d'identité */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Document d'identité</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Numéro de passeport *
                    {isFieldLocked('passportNumber') && <Lock className="inline w-3 h-3 ml-1" />}
                  </label>
                  <input
                    type="text"
                    value={formData.passportNumber || ''}
                    onChange={(e) => handleInputChange('passportNumber', e.target.value)}
                    disabled={isFieldLocked('passportNumber')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isFieldLocked('passportNumber') ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                    }`}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date d'expiration
                    {isFieldLocked('passportExpiry') && <Lock className="inline w-3 h-3 ml-1" />}
                  </label>
                  <input
                    type="date"
                    value={formData.passportExpiry || ''}
                    onChange={(e) => handleInputChange('passportExpiry', e.target.value)}
                    disabled={isFieldLocked('passportExpiry')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isFieldLocked('passportExpiry') ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                    }`}
                  />
                </div>
              </div>
              
              {/* Photo de la pièce d'identité - OBLIGATOIRE (Requirement 2.3) */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Camera className="inline w-4 h-4 mr-1" />
                  Photo de la pièce d'identité *
                </label>
                <div className={`border-2 border-dashed rounded-lg p-4 ${
                  validationErrors.identityPhotoUrl ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}>
                  {formData.documentPhotoUrl ? (
                    <div className="flex items-center gap-4">
                      <img
                        src={formData.documentPhotoUrl}
                        alt="Photo d'identité"
                        className="w-32 h-20 object-cover rounded-lg border border-gray-200"
                      />
                      <div className="flex-1">
                        <p className="text-sm text-green-600 font-medium">Photo téléchargée</p>
                        <button
                          type="button"
                          onClick={() => handleInputChange('documentPhotoUrl', '')}
                          className="text-sm text-red-600 hover:text-red-800 mt-1"
                          disabled={isFileClosed}
                        >
                          Supprimer et remplacer
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 mb-2">
                        Téléchargez une photo de la pièce d'identité
                      </p>
                      <label className="cursor-pointer">
                        <span className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2">
                          {uploadingPhoto ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Téléchargement...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4" />
                              Choisir un fichier
                            </>
                          )}
                        </span>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/jpg"
                          onChange={handlePhotoUpload}
                          className="hidden"
                          disabled={isFileClosed || uploadingPhoto}
                        />
                      </label>
                      <p className="text-xs text-gray-500 mt-2">
                        Formats acceptés: JPEG, PNG (max 5 Mo)
                      </p>
                    </div>
                  )}
                </div>
                {validationErrors.identityPhotoUrl && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.identityPhotoUrl}</p>
                )}
              </div>
            </div>

            {/* Séjour */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Séjour</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Numéro de chambre
                    {isFieldLocked('roomNumber') && <Lock className="inline w-3 h-3 ml-1" />}
                  </label>
                  <input
                    type="text"
                    value={formData.roomNumber || ''}
                    onChange={(e) => handleInputChange('roomNumber', e.target.value)}
                    disabled={isFieldLocked('roomNumber')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isFieldLocked('roomNumber') ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                    }`}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date d'arrivée
                    {isFieldLocked('checkInDate') && <Lock className="inline w-3 h-3 ml-1" />}
                  </label>
                  <input
                    type="date"
                    value={formData.checkInDate || ''}
                    onChange={(e) => handleInputChange('checkInDate', e.target.value)}
                    disabled={isFieldLocked('checkInDate')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isFieldLocked('checkInDate') ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                    }`}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de départ
                    {isFieldLocked('checkOutDate') && <Lock className="inline w-3 h-3 ml-1" />}
                  </label>
                  <input
                    type="date"
                    value={formData.checkOutDate || ''}
                    onChange={(e) => handleInputChange('checkOutDate', e.target.value)}
                    disabled={isFieldLocked('checkOutDate')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isFieldLocked('checkOutDate') ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 text-gray-600 hover:text-gray-900 transition-colors"
                disabled={saving}
              >
                Annuler
              </button>
              
              <button
                type="submit"
                disabled={saving || isFileClosed}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title={isFileClosed ? 'Impossible de modifier une fiche clôturée' : ''}
              >
                {isFileClosed && <Lock className="w-4 h-4" />}
                <Save className="w-4 h-4" />
                {saving ? 'Enregistrement...' : isFileClosed ? 'Fiche Clôturée' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GuestEditPage;
